import uniqBy from "lodash/uniqBy";

import Sentry from "@similarweb/sentry-logger";

import type { IAutocompleteKeyword } from "pro-features/components/AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";
import type { ISite } from "pro-features/components/Workspace/Wizard/src/types";
import type { DefaultFetchService } from "app/services/fetchService";
import { RecentService } from "app/services/recent/recentService";
import type { IRecentObject } from "app/@types/userdata";
import { KeywordsGroupUtilities } from "app/UtilitiesAndConstants/UtilityFunctions/KeywordsGroupUtilities";

export const getAutocompleteKeywordsForQuery = async (query: string, fetchService: DefaultFetchService) => {
    try {
        return await fetchService.get<ISite[]>(
            `/autocomplete/keywords?size=20&term=${encodeURIComponent(query)}&webSource=Desktop&validate=true`,
        );
    } catch (_e) {
        return [];
    }
};

const KEYWORDS_RECENT_TYPE = "keyword";

const keywordsTypeAndNonKeywordsGroupFilter = ({ data }: IRecentObject) => {
    const { type, keyword, comparedItems, keywordGroup } = data;
    return (
        type === KEYWORDS_RECENT_TYPE &&
        !keywordGroup &&
        // @ts-ignore FIXME: strict migration - Argument of type 'string | undefined' is not assignable to parameter of type 'string' — keyword is guaranteed present by the hasKeywordFilter applied upstream, and isKeywordsGroupByName tolerates undefined via optional chaining
        (comparedItems?.length || !KeywordsGroupUtilities.isKeywordsGroupByName(keyword))
    );
};

const keywordsComparisonFilter =
    (includeComparisons: boolean) =>
    ({ data }: IRecentObject) => {
        const { comparedItems } = data;
        return includeComparisons || comparedItems == null;
    };

const removeTopicTypeFilter = ({ data }: IRecentObject) => {
    return data.params?.isTopic !== "true";
};

const hasKeywordFilter = ({ data }: IRecentObject) => {
    return !!data.keyword;
};

export const getAutocompleteRecentKeywordSearches = (includeComparisons = false): IAutocompleteKeyword[] => {
    try {
        const recent = RecentService.getRecents();

        const keywordsTypeAndNonKeywordsGroupRecent = recent
            .filter(hasKeywordFilter)
            .filter(keywordsTypeAndNonKeywordsGroupFilter)
            .filter(keywordsComparisonFilter(includeComparisons))
            .filter(removeTopicTypeFilter);

        const dedupedRecentKeywordSearches = uniqBy(
            keywordsTypeAndNonKeywordsGroupRecent,
            ({ data: { keyword } }) => keyword,
        );

        return dedupedRecentKeywordSearches.map(({ data: { keyword } }) => ({
            name: keyword,
        })) as IAutocompleteKeyword[];
    } catch (error) {
        Sentry.errorTracker({
            e: error,
            message: "Failed to fetch autocomplete recent keyword searches",
            level: "error",
            params: {
                extra: {
                    includeComparisons,
                },
            },
        });
        return [];
    }
};
